import { useState, useCallback, useEffect } from "react";

// region Imports - Languages
import { ToastMessages } from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Shared / Utils
import api from "@services/api";
import { Plant } from "@shared/entities/reg_plants.entity";
import { Regional } from "@shared/interfaces/regional.interface";
// endregion Imports - Shared / Utils

interface UsePlantRegionalSyncProps {
  addToast: (props: any) => void;
  t: (key: string) => string;
}
export function usePlantRegionalSync({ addToast, t }: UsePlantRegionalSyncProps) {

  // region States
  const [optionsPlants, setOptionsPlants] = useState<Plant[]>([]);
  const [optionsRegionals, setOptionsRegionals] = useState<Regional[]>([]);

  const [activeOptionsPlants, setActiveOptionsPlants] = useState<Plant[]>([]);
  const [activeOptionsRegionals, setActiveOptionsRegionals] = useState<Regional[]>([]);

  const [openOptionsPlants, setOpenOptionsPlants] = useState(false);
  const [openOptionsRegionals, setOpenOptionsRegionals] = useState(false);

  const [plantToRegionalMap, setPlantToRegionalMap] = useState<Record<string, string>>({});
  const [checkAllPlants, setCheckAllPlants] = useState(false);
  // endregion States
  // region Constants
  const loadingOptionsPlants = openOptionsPlants && optionsPlants.length === 0;
  const loadingOptionsRegionals = openOptionsRegionals && optionsRegionals.length === 0;
  // endregion Constants
  // region Functions

  /**
   * Renders and updates the active regional options based on the currently selected plants.
   *
   * Dependencies:
   * - `activeOptionsPlants`: A list of currently selected plants with their identifiers (`id_plant`).
   * - `optionsRegionals`: A list of all regional options, each of which may include associated plants.
   */
  const renderActiveRegionals = useCallback(() => {
    const idPlants = new Set();
    const activeRegionals: Regional[] = [];

    if (activeOptionsPlants && optionsRegionals) {
      activeOptionsPlants.forEach(plant => {
        if (plant.id_plant) idPlants.add(plant.id_plant);
      });

      for (const regional of optionsRegionals) {
        let hasMissingPlant = false;

        for (const plant of (regional.plants || [])) {
          if (!idPlants.has(plant.id_plant)) {
            hasMissingPlant = true;
            break;
          }
        }

        if (hasMissingPlant) {
          continue;
        }

        if (regional.plants?.length) activeRegionals.push(regional);
      }

      setActiveOptionsRegionals(activeRegionals);
    }
  }, [activeOptionsPlants, optionsRegionals]);

  /**
   * Updates the state of active regional options and their associated plants based on the currently selected regionals.
   *
   * @param {Regional[]} regionals - An array of regional objects representing the selected regionals.
   *
   */
  const handleChangeRegional = useCallback((regionals: Regional[]) => {
    if (regionals.length > activeOptionsRegionals.length) {
      const activeOptionsRegionalsSet = new Set<string>(activeOptionsRegionals.map(activeOptionRegional => activeOptionRegional.id_regional));
      const newRegional = regionals.find((item) => !activeOptionsRegionalsSet.has(item.id_regional));
      const activePlantsSet = new Set(activeOptionsPlants.map(activeOptionPlant => activeOptionPlant.id_plant!));
      const newPlants: Plant[] = [];

      if (newRegional?.plants) {
        for (const plant of newRegional.plants) {
          if (!activePlantsSet.has(plant.id_plant!)) {
            newPlants.push(plant);
          }
        }
      }

      setActiveOptionsPlants([...activeOptionsPlants, ...newPlants]);
    } else {
      const regionalsSet = new Set<string>(regionals.map(regional => regional.id_regional));
      const removedRegional = activeOptionsRegionals.find((item) => !regionalsSet.has(item.id_regional));

      if (removedRegional?.plants) {
        const removedRegionalPlantsSet = new Set(removedRegional.plants.map((item: Plant) => item.id_plant));
        const newActiveOptionsPlants = activeOptionsPlants.filter(activeOptionPlant => !removedRegionalPlantsSet.has(activeOptionPlant.id_plant));

        setActiveOptionsPlants(newActiveOptionsPlants);
      }
    }

    setActiveOptionsRegionals(regionals);
  }, [activeOptionsRegionals, activeOptionsPlants]);

  /**
   * Update the state of active plant options and their associated regionals based on the currently selected plants.
   *
   * @param {Plant[]} plants - An array of plant objects representing the selected plants.
   */
  const handleChangePlants = useCallback((plants: Plant[]) => {
    if (plants.length > activeOptionsPlants.length) {
      const activeOptionsPlantsSet = new Set<string>(activeOptionsPlants.map(activeOptionPlant => activeOptionPlant!.id_plant!));
      const newPlant = plants.find((plant) => !activeOptionsPlantsSet.has(plant.id_plant!));

      if (newPlant) {
        const regionalId: string = plantToRegionalMap[newPlant.id_plant!];
        const regional = optionsRegionals.find(r => r.id_regional === regionalId);

        if (regional) {
          let addRegional = true;

          activeOptionsPlantsSet.add(newPlant.id_plant!); // Temporarily add a new one

          // Re-check logic: Do we have ALL plants for this regional now?
          // Actually, the original logic seemed to assume "if I add a plant, and it was the last one needed for regional, add regional"
          // Let's replicate original logic carefully
          const currentSelectedPlantsSet = new Set<string>(plants.map(p => p.id_plant!));

          for (const plant of (regional.plants || [])) {
            if (!currentSelectedPlantsSet.has(plant.id_plant!)) {
              addRegional = false;
              break;
            }
          }

          if (addRegional && !activeOptionsRegionals.some(r => r.id_regional === regional.id_regional)) {
            setActiveOptionsRegionals([...activeOptionsRegionals, regional]);
          }
        }
      }
    } else {
      const activeOptionsPlantsSet = new Set<string>(plants.map(activeOptionPlant => activeOptionPlant!.id_plant!));
      const removedIdPlant = activeOptionsPlants.find((item) => !activeOptionsPlantsSet.has(item.id_plant!))?.id_plant;

      if (removedIdPlant) {
        const regionalId: string = plantToRegionalMap[removedIdPlant];

        setActiveOptionsRegionals(activeOptionsRegionals.filter(activeOptionRegional => activeOptionRegional.id_regional !== regionalId));
      }
    }

    setActiveOptionsPlants(plants);
    setCheckAllPlants(plants.length === optionsPlants.length);
  }, [activeOptionsRegionals, activeOptionsPlants, plantToRegionalMap, optionsRegionals, optionsPlants.length]);

  /**
   * Handles the change event with a "select all plants" checkbox.
   * Updates the state of the application based on the checked status of the checkbox.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event - The change event triggered by the checkbox.
   */
  const handleCheckAllPlantsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckAllPlants(event.target.checked);

    if (event.target.checked) {
      setActiveOptionsPlants(optionsPlants);
      setActiveOptionsRegionals(optionsRegionals);
    } else {
      setActiveOptionsPlants([]);
      setActiveOptionsRegionals([]);
    }
  };
  //endregion Functions
  // region Effects

  // Map construction
  useEffect(() => {
    const plantToRegional: Record<string, string> = {};

    optionsRegionals.forEach(regional => {
      regional.plants?.forEach((plant: Plant) => {
        if (!plant.id_plant || !regional.id_regional) return;
        plantToRegional[plant.id_plant] = regional.id_regional;
      });
    });
    setPlantToRegionalMap(plantToRegional);
  }, [optionsRegionals]);

  // Fetching regionals when opening the modal
  useEffect(() => {
    const fetchRegionals = async () => {
      if (optionsRegionals.length) {
        renderActiveRegionals();

        return;
      }

      try {
        const { data } = await api.get("regionals/read", {
          params: { includePlants: true }
        });

        if (data.status === "success") {
          setOptionsRegionals(data.result);
        }
      } catch (error) {
        addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      }
    };

    fetchRegionals().then();
  }, [activeOptionsPlants.length, optionsRegionals.length, renderActiveRegionals]);

  // Fetching plants when opening the modal
  useEffect(() => {
    if (!openOptionsPlants) {
      setOptionsPlants([]);

      return;
    }

    let active = true;

    (async () => {
      try {
        const { data } = await api.get("plants/read");

        if (data.status === "success" && active) {
          setOptionsPlants(data.result);
        } else if (active) {
          setOpenOptionsPlants(false);
          addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
        }
      } catch (error) {
        if (active) {
          setOpenOptionsPlants(false);
          if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
          else addToast({ type: "error", title: error.response?.data?.backend, description: error.response?.data?.message });
        }
      }
    })();

    return () => { active = false; };
  }, [openOptionsPlants, addToast, t]);
  // endregion Effects

  return {
    optionsPlants,
    setOptionsPlants,
    optionsRegionals,
    setOptionsRegionals,
    activeOptionsPlants,
    setActiveOptionsPlants,
    activeOptionsRegionals,
    setActiveOptionsRegionals,
    checkAllPlants,
    setCheckAllPlants,
    openOptionsPlants,
    setOpenOptionsPlants,
    openOptionsRegionals,
    setOpenOptionsRegionals,
    loadingOptionsPlants,
    loadingOptionsRegionals,
    handleChangeRegional,
    handleChangePlants,
    handleCheckAllPlantsChange
  };
}
